// Sandbox-only "Verify Identity" Power-Up.
//
// Per PROTOCOL_SPEC.md §1 the sandbox branch defaults citizens to
// OBSERVER (1.0× weight). The Power-Up simulates a Coinbase EAS check
// with a 3-second fake progress bar, then promotes the citizen to
// CITIZEN tier (1.5× weight) via setForcedTier — so `identity.tier`
// stays the single source of truth for the multiplier, and no code
// path needs to consult a parallel "verification level" store.
//
// v2.3.1 Minting Cockpit Activation — the Power-Up ALSO injects a
// synthetic 150 DP so the Dynamic Bond demo can resolve to a
// discounted creation bond (500 − 150×0.5 = 425 OC) immediately on
// promotion. The override is cleared on RESET TO OBSERVER.
//
// Testers can reset by running `window.agoracle.resetPowerUp()` — the
// helper clears `agoracle_identity_tier` and the simulated-DP override
// so the next probe falls back to the OBSERVER default.

import { useState } from 'react';
import { IdentityLevel, setForcedTier } from './identity';
import { setSimulatedDP, clearSimulatedDP, SIMULATED_CITIZEN_DP } from './ledger';

const ACTIVATION_MS = 3000;

export interface PowerUpState {
  activating:       boolean;
  progress:         number;      // 0..100
  promoteToCitizen: (userId?: string) => Promise<boolean>;  // true on first-time promotion
}

export function useCitizenPromotion(): PowerUpState {
  const [activating, setActivating] = useState(false);
  const [progress,   setProgress]   = useState(0);

  async function promoteToCitizen(userId?: string): Promise<boolean> {
    if (activating) return false;
    setActivating(true);
    setProgress(0);

    const started = Date.now();
    const tick = window.setInterval(() => {
      const pct = Math.min(100, ((Date.now() - started) / ACTIVATION_MS) * 100);
      setProgress(pct);
    }, 50);

    await new Promise(resolve => window.setTimeout(resolve, ACTIVATION_MS));
    window.clearInterval(tick);
    setProgress(100);
    if (userId) setSimulatedDP(userId, SIMULATED_CITIZEN_DP);
    setForcedTier(IdentityLevel.CITIZEN);
    setActivating(false);
    return true;
  }

  return { activating, progress, promoteToCitizen };
}

// Dev helper — lets Dromide drop back to OBSERVER from DevTools without
// nuking the whole ledger. Registered once on first import.
if (typeof window !== 'undefined') {
  const w = window as unknown as { agoracle?: Record<string, unknown> };
  w.agoracle = w.agoracle ?? {};
  w.agoracle.resetPowerUp = (userId?: string): void => {
    setForcedTier(null);
    if (userId) clearSimulatedDP(userId);
  };
}
